import { Button } from '@heroui/react';
import type { AiUsageCard } from '../../api/ai';
import {
  getUsageStatusLabel,
  getUsageTypeLabel,
} from '../my-parking-spots/utils/usage-display';
import {
  ASSISTANT_APPROVE_REQUEST_LABEL,
  ASSISTANT_CANCEL_REQUEST_LABEL,
  ASSISTANT_REJECT_REQUEST_LABEL,
} from './assistant-constants';
import { formatChatPeriod } from './assistant-spot-format';

interface AssistantUsageCardsProps {
  usages: AiUsageCard[];
  onCancel?: (usageId: string) => void;
  canCancel?: (usage: AiUsageCard) => boolean;
  onApprove?: (usageId: string) => void;
  canApprove?: (usage: AiUsageCard) => boolean;
  onReject?: (usageId: string) => void;
  canReject?: (usage: AiUsageCard) => boolean;
  embedded?: boolean;
}

const STATUS_CLASS = {
  PENDING: 'bg-warning/15 text-warning',
  ACTIVE: 'bg-success/15 text-success',
  CANCELLED: 'bg-danger/15 text-danger',
  FINISHED: 'bg-default/15 text-muted',
  REJECTED: 'bg-danger/15 text-danger',
} as const;

export function AssistantUsageCards({
  usages,
  onCancel,
  canCancel,
  onApprove,
  canApprove,
  onReject,
  canReject,
  embedded = false,
}: AssistantUsageCardsProps) {
  if (usages.length === 0) return null;

  return (
    <div
      className={`flex flex-col gap-2 ${embedded ? 'w-full' : 'w-full max-w-[90%] self-start'}`}
    >
      {usages.map((usage) => {
        const showCancel =
          onCancel != null && (canCancel ? canCancel(usage) : usage.canCancel);
        const showApprove =
          onApprove != null && (canApprove ? canApprove(usage) : usage.canApprove === true);
        const showReject =
          onReject != null && (canReject ? canReject(usage) : usage.canReject === true);

        return (
          <article
            key={usage.id}
            className={`rounded-2xl border border-separator px-3 py-2.5 ${
              embedded ? 'bg-surface' : 'bg-surface shadow-sm'
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[15px] font-semibold leading-tight tracking-tight">
                  Vaga {usage.spotCode}
                </p>
                <p className="mt-0.5 text-xs text-muted">
                  {getUsageTypeLabel(usage.type)}
                  {usage.requesterLabel ? ` · ${usage.requesterLabel}` : ''}
                </p>
              </div>
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_CLASS[usage.status]}`}
              >
                {getUsageStatusLabel(usage.status)}
              </span>
            </div>

            <p className="mt-2 text-[13px] font-medium tabular-nums leading-snug text-foreground">
              {formatChatPeriod(usage.startAt, usage.endAt)}
            </p>

            {showApprove || showReject ? (
              <div className="mt-2.5 flex gap-2">
                {showReject ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="h-8 flex-1"
                    onPress={() => onReject?.(usage.id)}
                  >
                    {ASSISTANT_REJECT_REQUEST_LABEL}
                  </Button>
                ) : null}
                {showApprove ? (
                  <Button
                    size="sm"
                    variant="primary"
                    className="h-8 flex-1"
                    onPress={() => onApprove?.(usage.id)}
                  >
                    {ASSISTANT_APPROVE_REQUEST_LABEL}
                  </Button>
                ) : null}
              </div>
            ) : null}

            {showCancel ? (
              <Button
                size="sm"
                variant="secondary"
                className="mt-2.5 h-8 w-full"
                onPress={() => onCancel?.(usage.id)}
              >
                {ASSISTANT_CANCEL_REQUEST_LABEL}
              </Button>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}
